
import React, { useEffect, useState, useCallback, useMemo, use } from "react";
import {
    Box,
    Typography,
    CardContent,
    useTheme,
    useMediaQuery,
    Fade,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    FormControl,
    Button,
    Chip,
    Grid,
    Card,
    styled,
    CircularProgress,
    SelectChangeEvent,
    Tooltip,
    TextField,
    InputAdornment,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";

import AdminApi from "Service/Admin.api";
import { HeaderCard, SearchCard, StyledContainer } from "./ProfessionalUserList";
import ColorData from "Components/CstmTheme/ColorData";
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import { StatusChip } from "Pages/OrderHistory/OrderHistory";
import { getPaymentStatusDetails, getStatusColorHex } from "Utils/Common";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

type CaseItem = {
    pkCase: string;
    caseNumber: string;
    description: string;
    assignedProfessionalId?: string;
    casePrice: number;
    dateCreated: string;
    statusLastUpdated: string;
    casePaymentStatus: number;
     currentProcessingStatus: {
            "pkProcessingStatus": number;
            "statusName": string;
        },
    client: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
        contactNumber: string;
    };
    caseType: {
        pkCaseType: number;
        caseTypeName: string;
    };
    orderType: {
        pkOrderType: number;
        orderTypeName: string;
        orderPrice: number;
    };
    assignedProfessional?: {
        id: number;
        firstName: string;
        lastName: string;
    };
};
interface Professional {
    id: string;
    firstName: string;
    lastName: string;
}



const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease-in-out",
    "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
    },
}));

const AssignButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    textTransform: "none",
    fontWeight: "bold",
    padding: theme.spacing(1, 3),
    background: ColorData.primary.main, // <-- Use secondary color for background
    color: ColorData.text.secondary, // <-- Set contrasting text color (black)
    "&:hover": {
        background: ColorData.hover.main, // <-- Use hover color for background on hover
        transform: "translateY(-1px)",
        boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
        color: ColorData.text.primary, // <-- Maintain contrast on hover too
    },
}));

// Custom hook for responsive table configuration
const useResponsiveTable = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const tableConfig = useMemo(() => ({
        showScrollbars: isDesktop,
        maxHeight: isMobile ? '60vh' : isTablet ? '65vh' : '70vh',
        minWidth: isMobile ? '100%' : '800px',
        stickyHeader: !isMobile,
        useCardLayout: isMobile || isTablet,
    }), [isMobile, isTablet, isDesktop]);

    return { isMobile, isTablet, isDesktop, tableConfig };
};

// Custom hook for scroll performance monitoring
const useScrollPerformance = () => {
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const handleScroll = useCallback(() => {
        setIsScrolling(true);

        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            setIsScrolling(false);
        }, 150);
    }, []);

    React.useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    return { isScrolling, handleScroll };
};

const AssignProfessional: React.FC = () => {
    const navigate = useNavigate();
    const [caseList, setCaseList] = useState<CaseItem[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<CaseItem[]>([]);
    const [professionalList, setProfessionalList] = useState<Professional[]>([]);
    const [selectedProfessionals, setSelectedProfessionals] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [assignLoading, setAssignLoading] = useState<Record<string, boolean>>({});
    const dispatch = useDispatch();
    const theme = useTheme();

    // Use responsive table configuration
    const { isMobile, isTablet, tableConfig } = useResponsiveTable();
    const { isScrolling, handleScroll } = useScrollPerformance();

    // Keyboard navigation state
    const [focusedCell, setFocusedCell] = useState<{ row: number; col: number } | null>(null);

    const ITEM_HEIGHT = 40; // Adjust if needed

    // Keyboard navigation handler
    const handleKeyboardNavigation = useCallback((event: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
        const { key, ctrlKey } = event;
        const totalRows = caseList.length;
        const totalCols = 8; // Number of columns

        let newRow = rowIndex;
        let newCol = colIndex;

        switch (key) {
            case 'ArrowRight':
                event.preventDefault();
                newCol = Math.min(colIndex + 1, totalCols - 1);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                newCol = Math.max(colIndex - 1, 0);
                break;
            case 'ArrowDown':
                event.preventDefault();
                newRow = Math.min(rowIndex + 1, totalRows - 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                newRow = Math.max(rowIndex - 1, 0);
                break;
            case 'Home':
                event.preventDefault();
                if (ctrlKey) {
                    newRow = 0;
                    newCol = 0;
                } else {
                    newCol = 0;
                }
                break;
            case 'End':
                event.preventDefault();
                if (ctrlKey) {
                    newRow = totalRows - 1;
                    newCol = totalCols - 1;
                } else {
                    newCol = totalCols - 1;
                }
                break;
            default:
                return;
        }

        setFocusedCell({ row: newRow, col: newCol });

        // Focus the new cell
        const cellId = `cell-${newRow}-${newCol}`;
        const cellElement = document.getElementById(cellId);
        if (cellElement) {
            cellElement.focus();
        }
    }, [caseList.length]);

useEffect(() => {
    fetchAllUnassignedData();
}, []);

const displayedCases = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    if (!query) return caseList;

    return caseList.filter((caseItem) => {
        const firstName = caseItem?.client?.firstName ?? "";
        const lastName = caseItem?.client?.lastName ?? "";
        const fullName = `${firstName} ${lastName}`.toLowerCase();
        const caseId = caseItem?.pkCase?.toString() ?? "";
        const phone = (caseItem?.client?.contactNumber ?? "").toLowerCase();
        const orderType = (caseItem?.orderType?.orderTypeName ?? "").toLowerCase();
        const caseType = (caseItem?.caseType?.caseTypeName ?? "").toLowerCase();
        const caseNumber = (caseItem?.caseNumber ?? "").toLowerCase();

        return (
            fullName.includes(query) ||
            phone.includes(query) ||
            orderType.includes(query) ||
            caseType.includes(query) ||
            caseId.includes(query) ||
            caseNumber.includes(query)
        );
    });
}, [searchQuery, caseList]);

    const fetchAllUnassignedData = async () => {
        const res = await AdminApi.getAllUnAssignedProfessionalCases();
        if (res?.success && Array.isArray(res.data)) {
            // Sort cases by latest first
            const sortedCases = res.data.sort(
                (a: any, b: any) => b.pkCase - a.pkCase
            );

            setCaseList(sortedCases);
            setFilteredUsers(sortedCases as any);
        }
    };

    const renderMobileCard = (caseItem: CaseItem, index: number) => (
        <StyledCard 
        key={caseItem.pkCase} 
        sx={{ 
            width: '100%',      // Ensures card takes full container width
            mb: 2,              // Adds spacing between cards
            boxSizing: 'border-box' 
        }}    >
            <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" mb={2}>
                    <Chip label={`#${index + 1}`} size="small" color="primary" sx={{ fontWeight: "bold" }} />
                    <Chip
                        label={caseItem.orderType.orderTypeName}
                        variant="outlined"
                        size="small"
                        color="secondary"
                    />
                </Box>

                <Typography variant="h6" color="primary" fontWeight="bold">
                    {caseItem.caseType.caseTypeName}
                </Typography>

                <Box mb={2}>
                    <Typography variant="body2" gutterBottom>
                        <strong>Client:</strong> {caseItem.client.firstName} {caseItem.client.lastName}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>Phone:</strong> {caseItem.client.contactNumber}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>Case Price:</strong> {caseItem.casePrice}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="bold">Payment Status:</Typography>
                        {(() => {
                            const status = getPaymentStatusDetails(caseItem.casePaymentStatus);
                            return (
                                <Chip
                                    label={status.label}
                                    size="small"
                                    sx={{ bgcolor: status.bg, color: status.color, fontWeight: 'bold' }}
                                />
                            );
                        })()}
                    </Box>
                    <Typography variant="body2" gutterBottom>
                        <strong>Case Status:</strong> {caseItem.currentProcessingStatus.statusName}
                    </Typography>
                </Box>
                   
                <TableCell align="center">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    onClick={() => navigate(`/case-details/${caseItem.pkCase}`)}
                                >
                                    View
                                </Button>
                            </TableCell>
                
            </CardContent>
        </StyledCard>
    );


    const renderDesktopTable = () => (
        <TableContainer
            sx={{
                maxHeight: 470,
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
            }}
        >
            <Table stickyHeader aria-label="Cases assignment table">
                {/* TABLE HEAD */}
                <TableHead>
                    <TableRow
                        sx={{
                            "& .MuiTableCell-root": {
                                backgroundColor: ColorData.primary.main + " !important", // Force override
                                color: ColorData.text.secondary, // white
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                fontSize: "0.875rem",
                                borderBottom: `1px solid ${ColorData.primary.strokeColor}`,
                            },
                        }}
                    >
                        <TableCell>Case No.</TableCell>
                        <TableCell>Order Type</TableCell>
                        <TableCell>Case Type</TableCell>
                        <TableCell>Case Price</TableCell>
                        <TableCell>Payment Status</TableCell>
                        <TableCell>Client Name</TableCell>
                        <TableCell>Client Phone</TableCell>
                        <TableCell>Case Status</TableCell>
                        <TableCell align="center">Assign Professional</TableCell>
                        <TableCell align="center">Action</TableCell>
                    </TableRow>
                </TableHead>

                {/* TABLE BODY */}
                <TableBody>
                    {displayedCases.map((item, rowIndex) => (
                        <TableRow
                            key={item.pkCase}
                            hover
                            sx={{
                                backgroundColor: ColorData.background.default,
                                transition: "background-color 0.3s, transform 0.3s",
                                "&:hover": {
                                    backgroundColor: ColorData.hover.main,
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                                },
                            }}
                        >
                            <TableCell>
                                <Chip label={item.caseNumber ? item.caseNumber : item.pkCase} size="small" color="primary" />
                            </TableCell>
                            <TableCell sx={{ width: 150, maxWidth:  250}}>
                                <Chip
                                    label={item.orderType.orderTypeName}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                />
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" fontWeight="medium" color="primary">
                                    {item.caseType.caseTypeName}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{fontWeight: "bold"}}>
                                {item.casePrice}
                            </TableCell>

                            <TableCell>
                                {(() => {
                                    const { label, color, bg } = getPaymentStatusDetails(item.casePaymentStatus);

                                    return (
                                        <StatusChip
                                            label={label}
                                            sx={{
                                                background: bg,
                                                color: color,
                                                fontWeight: "bold",
                                                fontSize: "0.75rem",
                                                borderRadius: "6px",
                                                textTransform: "uppercase",
                                                whiteSpace: "nowrap"
                                            }}
                                        />
                                    );
                                })()}
                            </TableCell>

                            <TableCell>
                                {item.client.firstName} {item.client.lastName}
                            </TableCell>
                            <TableCell>{item.client.contactNumber}</TableCell>
                            <TableCell>
                                <StatusChip
                                    label={item.currentProcessingStatus.statusName}
                                    status={item.currentProcessingStatus.statusName}
                                />
                            </TableCell>

                            {/* Assigned Professional Status */}
                            <TableCell align="center">
                                {item.assignedProfessionalId === null ? (
                                    <Chip
                                        label="Not Assigned"
                                        color="error"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    />
                                ) : (
                                    <Chip
                                        label="Assigned"
                                        color="success"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    />
                                )}
                            </TableCell>
                            <TableCell align="center">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    onClick={() => navigate(`/case-details/${item.pkCase}`)}
                                    // Disable only if status is Pending (0) AND price is specifically more than 0
                                    // disabled={item.casePaymentStatus === 0 && item.casePrice > 0}
                                >
                                    Assign
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    if (loading) {
        return (
            <StyledContainer maxWidth="xl">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress size={60} />
                </Box>
            </StyledContainer>
        );
    }

    return (
        <Box sx={{ padding: 1 }}>

             {/* 3. ADDED BACK BUTTON HERE */}
             <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate(-1)} // Goes back to previous page
                    sx={{ fontWeight: 'bold' }}
                >
                    Back
                </Button>
            </Box>


             <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: theme => ColorData.primary.main,
                    color: theme => ColorData.text.secondary,
                  }}>
                    Assign Professional to Case
                  </Typography>

            {/* Search and Actions */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 8 }}>
                    {/* <SearchCard elevation={2}>
                                <CardContent> */}
                    <TextField
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search professional User..."
                        variant="outlined"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="primary" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main,
                                },
                            },
                        }}
                    />
                    {/* </CardContent>
                            </SearchCard> */}
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Box display="flex" gap={1} height="100%" alignItems="center">
                        <Chip
                            icon={<PersonIcon />}
                            label={`Total Cases: ${caseList.length}`}
                            color="info"
                            variant="outlined"
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                height: { xs: 28, sm: 32 }
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>

            {displayedCases.length === 0 ? (
                <Box textAlign="center" py={8}>
                    <Typography variant="h6" color="black" gutterBottom>
                        No cases available
                    </Typography>
                    <Typography variant="body2" color="black">
                        Cases will appear here once they are created.
                    </Typography>
                </Box>
            ) : (
                tableConfig.useCardLayout ? (
                    <Box
                        sx={{
                            maxHeight: tableConfig.maxHeight,
                            overflow: 'auto',
                            WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: theme.palette.grey[400],
                                borderRadius: '3px',
                                '&:hover': {
                                    background: theme.palette.grey[600],
                                },
                            },
                        }}
                    >
                        <Grid container spacing={2}>
                            {displayedCases.map((caseItem, index) => (
                                <Grid key={caseItem.pkCase} sx={{width: '100%'}}>
                                    {renderMobileCard(caseItem, index)}
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ) : (
                    renderDesktopTable()
                )
            )}
        </Box>
        
    );
};

export default AssignProfessional;
